import logo from "./assets/ems/logo.png";
import login from "./assets/ems/login.png";
import dashboard from "./assets/ems/admin-dashboard.png";
import payroll from "./assets/ems/payroll.png";
import profile from "./assets/ems/employee-profile.png";
import staff from "./assets/ems/staff-dashboard.png";

import leaveRequest from "./assets/ems/request-leave.png";
import overtimeRequest from "./assets/ems/request-overtime.png";
import timelogRequest from "./assets/ems/request-timelog.png";
import payrollManager from "./assets/ems/payroll-manager.png";

const pages = [
  {
    title: "Login & Attendance Portal",
    image: login,
    description:
      "An employee sign-in and timekeeping page for recording time in, breaks, and time out, with location permission and an image required for attendance submission.",
  },
  {
    title: "Admin Dashboard",
    image: dashboard,
    description:
      "A central view of employee totals, daily attendance, pending requests, payroll progress, hiring trends, and searchable time logs.",
  },
  {
    title: "Payroll Manager",
    image: payrollManager,
    description:
      "An admin workspace for generating payroll, viewing payroll summaries, completing payroll, emailing payslips, printing payslips, and deleting payroll records. Filters help locate records by employee, payroll type, date, and status.",
  },
  {
    title: "Payroll & Payslip Management",
    image: payroll,
    description:
      "A detailed payslip view showing earnings, deductions, allowances, adjustments, and net pay, with tools for managing additional deductions and notes for the next payroll.",
  },
  {
    title: "Employee Profile",
    image: profile,
    description:
      "An employee record bringing together personal information, employment details, pay rates, schedules, government IDs, documents, benefits, allowances, and leave credits.",
  },
  {
    title: "Staff Dashboard",
    image: staff,
    description:
      "An employee self-service dashboard for checking schedules, attendance status, leave balances, requests, announcements, and the latest payslip, with shortcuts for time-log corrections and overtime requests.",
  },
  {
    title: "Staff Leave Requests",
    image: leaveRequest,
    description:
      "Staff can submit leave requests with the leave type, dates, and reason, then track the assigned approver and approval status. Date and status filters make it easy to find past requests.",
  },
  {
    title: "Staff Overtime Requests",
    image: overtimeRequest,
    description:
      "Staff can request overtime by specifying the start and end date and time, providing a reason, and tracking the assigned approver and request status.",
  },
  {
    title: "Staff Time-log Requests",
    image: timelogRequest,
    description:
      "Staff can request corrections for missed time-in or time-out records, specifying the date, time, and reason, then track the request through approval.",
  },
];

export default function EMSProject({ onContact }) {
  return (
    <article className="overflow-hidden">
      <div className="grid gap-6 border-b border-white/10 p-6 sm:p-8 md:grid-cols-[180px_1fr] md:items-center">
        <div className="flex items-center justify-center rounded-2xl bg-black p-5">
          <img
            src={logo}
            alt="EMS — Employee Management System"
            className="h-auto w-full max-w-[180px]"
          />
        </div>
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a8cdef]">
            Featured software project
          </span>
          <h4 className="mt-4 text-sm font-semibold text-slate-100">
            Project overview
          </h4>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">
            EMS is a centralized Employee Management and Payroll System designed
            to simplify workforce administration in one secure platform. It
            enables administrators to manage employee records, attendance,
            timekeeping, leave, overtime, allowances, deductions, payroll
            processing, and payslips while giving employees access to their own
            records and requests through a self-service portal.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            The system supports multiple attendance methods, including biometric
            fingerprint, RFID, and web-based time logging. For field-based
            employees, web attendance can capture the employee’s current
            location and photo for additional verification.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            EMS also streamlines payroll operations by using recorded attendance
            and approved employee requests as part of payroll preparation.
            Administrators can generate, review, adjust, and complete payroll
            periods, while employees can securely view their payslips and
            attendance information online.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              "Employee records",
              "Attendance",
              "Payroll",
              "Staff self-service",
            ].map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-sky-400/10 px-3 py-1 text-xs text-sky-200"
              >
                {tag}
              </span>
            ))}
          </div>
          <a
            href="#contact"
            onClick={onContact}
            className="mt-5 inline-flex items-center justify-center rounded-full bg-[#2E7EAE] px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-300"
          >
            Contact for a free demo now
          </a>
        </div>
      </div>
      <div className="p-6 sm:p-8">
        <h4 className="text-lg font-semibold text-white">Inside EMS</h4>
        <p className="mt-2 text-sm text-slate-400">
          A look at the admin and employee experience. Select a screenshot to
          view the full page.
        </p>
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          {pages.map((page) => (
            <figure
              key={page.title}
              className="overflow-hidden rounded-2xl border border-white/10 bg-[#07141f]/60"
            >
              <a
                href={page.image}
                target="_blank"
                rel="noreferrer"
                aria-label={`View full screenshot: ${page.title} (opens in a new tab)`}
                className="block overflow-hidden bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-300"
              >
                <img
                  src={page.image}
                  alt={`${page.title} interface in EMS`}
                  loading="lazy"
                  className="h-56 w-full object-cover object-top transition duration-300 hover:scale-[1.03]"
                />
              </a>
              <figcaption className="p-5">
                <h5 className="font-semibold text-slate-50">{page.title}</h5>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  {page.description}
                </p>
                <a
                  href={page.image}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-block text-xs font-semibold text-sky-300 hover:underline"
                >
                  View full screenshot ↗
                  <span className="sr-only">
                    {" "}
                    of {page.title} (opens in a new tab)
                  </span>
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </article>
  );
}
